'use client'

import React from 'react'
import styles from './styles.module.css'
import Img from '../../../Img'
import SmartLink from '../../../SmartLink'
import SaleCountdown from '../../../SaleCampaign/SaleCountdown'
import useSaleClock from '../../../SaleCampaign/useSaleClock'
import DealPrice from '../../../SaleCampaign/DealPrice'
import { useRegionOffers } from '../../../RegionOffers'
import { useUrlRegion } from '../../../../utils/useCurrentRegion'
import { pathWithRegion } from '../../../../utils/regions'
import {
  getCampaignPhase, getLimitedDeals, getLimitedDealState, dealPriceOf,
  PHASE_LIVE, PHASE_TEASER, PHASE_ENDED, DEAL_LIVE, DEAL_UPCOMING, msOf,
} from '../../../../lib/saleCampaign'
import { formatSaleRange, itemTypeOf, regularPriceOf, salePriceOf, savingOf } from '../saleFormat'

// The headline limited deal (the first one) as { title, price, regular } for
// the page hero, or null. Same price rules as SaleLimitedDeals: the backend
// price while live, the published deal price in the teaser, nothing while the
// backend price is not loaded or the deal is sold out.
function headlineDealOf(campaign, nowMs, region, offers) {
  const deal = getLimitedDeals(campaign, region)[0]
  const item = deal && (offers || []).find((o) => itemTypeOf(o) === deal.itemType)
  if (!item || !item.price) return null
  const state = getLimitedDealState(campaign, nowMs, region, deal, item)
  const regular = regularPriceOf(item)
  const title = deal.title || item.namePublicShop || item.name
  if (state === DEAL_LIVE && savingOf(item)) return { title, price: salePriceOf(item), regular }
  const published = dealPriceOf(deal, region, regular && regular.currency)
  if (state === DEAL_UPCOMING && published && regular && published.priceCents < regular.priceCents) {
    return { title, price: published, regular }
  }
  return null
}

// Sale campaign hero. Two looks from one section type:
//  - variant "page" (default): the top of /<region>/<campaign.route> — h1, dates,
//    big countdown, "3 ways to save" strip.
//  - variant "home": the homepage banner — shorter, CTA to the sale page.
// Renders NOTHING outside the campaign window, so it can sit on the homepage
// year-round and switch itself on/off with the dates in the `sale-campaign` doc.
//
// Copy comes from the campaign doc (`hero` / `home`), with the section's own
// fields as overrides. Image = section `imageCs` (a dataset image ref).
function SaleHero({ saleCampaign: campaign, saleNowMs, saleClockOffsetMs, variant = 'page', imageCs, titleOverride }) {
  const { nowMs, mounted } = useSaleClock(saleNowMs, saleClockOffsetMs)
  const urlRegion = useUrlRegion()
  const { offers } = useRegionOffers()
  const phase = getCampaignPhase(campaign, nowMs)
  const isHome = variant === 'home'
  if (phase !== PHASE_LIVE && phase !== PHASE_TEASER) {
    // The homepage banner simply disappears. The sale PAGE keeps a minimal hero
    // (one h1, a way out) so a late visitor or an old ad click never lands on a
    // blank page.
    if (isHome || !campaign) return null
    const ended = phase === PHASE_ENDED
    return (
      <section className={[styles.hero, styles.page, styles.off].join(' ')}>
        <div className={styles.inner}>
          <div className={styles.copy}>
            <h1 className={styles.title}>{campaign.name}</h1>
            <p className={styles.text}>
              {ended
                ? ((campaign.hero && campaign.hero.textEnded) || 'This sale has ended. Thank you to everyone who joined us.')
                : `Starts ${formatSaleRange(campaign.start, campaign.end)}.`}
            </p>
            <div className={styles.ctas}>
              <SmartLink href={urlRegion ? pathWithRegion('/shop', urlRegion) : '/shop'} className={styles.ctaPrimary}>Go to the shop</SmartLink>
            </div>
          </div>
        </div>
      </section>
    )
  }

  const copy = (isHome ? campaign.home : campaign.hero) || {}
  const live = phase === PHASE_LIVE
  const salePath = `/${campaign.route}`
  // Region pages link inside their store; the bare homepage link 307s to the
  // visitor's store (commerce path), so no region guess is needed there.
  const saleHref = urlRegion ? pathWithRegion(salePath, urlRegion) : salePath
  const hasLimited = getLimitedDeals(campaign, urlRegion).length > 0

  const title = titleOverride || (live ? copy.titleLive : copy.titleTeaser) || campaign.name
  const subtitle = live ? copy.subtitleLive : copy.subtitleTeaser
  const text = live ? copy.textLive : copy.textTeaser
  const ctaLabel = live ? (copy.ctaLive || 'Shop the deals') : (copy.ctaTeaser || 'See what is coming')
  const target = live ? msOf(campaign.end) : msOf(campaign.start)
  const Heading = isHome ? 'h2' : 'h1'
  const headline = isHome ? null : headlineDealOf(campaign, nowMs, urlRegion, offers)

  const actions = (
    <>
      <div className={styles.countdownRow}>
        <SaleCountdown
          targetMs={target}
          nowMs={nowMs}
          mounted={mounted}
          size={isHome ? 'md' : 'lg'}
          theme="dark"
          label={live ? (copy.countdownLiveLabel || 'Sale ends in') : (copy.countdownTeaserLabel || 'Starts in')}
        />
      </div>
      <div className={styles.ctas}>
        {(isHome || !live) && (
          <SmartLink href={isHome ? saleHref : '#limited-deals'} className={styles.ctaPrimary}>{isHome ? ctaLabel : (copy.ctaTeaserPage || 'See the deals')}</SmartLink>
        )}
        {!isHome && live && (
          <SmartLink href={hasLimited ? '#limited-deals' : '#all-deals'} className={styles.ctaPrimary}>{hasLimited ? (copy.ctaLimited || 'See the pedal deals') : ctaLabel}</SmartLink>
        )}
        {/* the full price grid is optional: no `ctaAll` copy, no link to it */}
        {!isHome && live && copy.ctaAll && (
          <SmartLink href="#all-deals" className={styles.ctaSecondary}>{copy.ctaAll}</SmartLink>
        )}
      </div>
    </>
  )

  return (
    <section className={[styles.hero, isHome ? styles.home : styles.page].join(' ')} aria-label={campaign.name}>
      <div className={styles.glow} aria-hidden="true"/>
      <div className={styles.inner}>
        <div className={styles.copy}>
          <div className={styles.eyebrow}>
            <span className={styles.dot} aria-hidden="true"/>
            {live ? (copy.eyebrowLive || 'Live now') : (copy.eyebrowTeaser || 'Coming soon')}
            <span className={styles.dates}>{formatSaleRange(campaign.start, campaign.end)}</span>
          </div>
          <Heading className={styles.title}>{title}</Heading>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          {text && <p className={styles.text}>{text}</p>}
          {headline && (
            <div className={styles.headlineDeal}>
              <span className={styles.headlineName}>{headline.title}</span>
              <DealPrice price={headline.price} regular={headline.regular} size="xl"/>
            </div>
          )}
          {!isHome && actions}
          {Array.isArray(copy.bullets) && copy.bullets.length > 0 && (
            <ul className={styles.bullets}>
              {copy.bullets.map((b) => <li key={b}>{b}</li>)}
            </ul>
          )}
        </div>
        {isHome && <div className={styles.homeActions}>{actions}</div>}
        {imageCs && imageCs.image && (
          <div className={styles.media}>
            <Img image={imageCs.image} alt={imageCs.alt || campaign.name} objectFit="contain" sizes="(min-width: 1024px) 55vw, 100vw"/>
          </div>
        )}
      </div>
    </section>
  )
}

export default SaleHero
